/*
 * Les icônes, dessinées à la main plutôt qu'importées d'une bibliothèque.
 *
 * Elles sont neuf, elles font 200 octets chacune, et une bibliothèque
 * apporterait deux mille icônes dans un style qui n'est pas celui de la
 * marque. Toutes en trait, jamais pleines — c'est la même règle que pour la
 * couleur : ce que l'interface dessine, elle l'émet.
 */

type IconProps = { size?: number; className?: string }

function Svg({ size = 16, children, className }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

export const IconToday = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Svg>
)

export const IconCalendar = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </Svg>
)

export const IconTicket = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 9V7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2a2.5 2.5 0 0 0 0 5v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3a2.5 2.5 0 0 0 0-5Z" />
    <path d="M14 6v2M14 11v2M14 16v2" strokeDasharray="1 2.5" />
  </Svg>
)

export const IconPeople = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
    <path d="M16 5.5a3.2 3.2 0 0 1 0 6M17.5 15.2A5.5 5.5 0 0 1 20.5 20" />
  </Svg>
)

/* UNE personne : ses paramètres à elle, pas ceux de l'appareil (16/10). */
export const IconPerson = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="7.5" r="3.5" />
    <path d="M5 20a7 7 0 0 1 14 0" />
  </Svg>
)

export const IconCompass = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="m15.5 8.5-2 5-5 2 2-5Z" />
  </Svg>
)

/*
 * Un œil, pour « voir comme ».
 *
 * Le bridage volontaire n'est ni un réglage ni une personne : c'est un point
 * de vue qu'on emprunte. Aucune des icônes existantes ne le disait — la roue
 * aurait promis un réglage, le bonhomme aurait promis un changement de compte,
 * qui est exactement ce que le dispositif ne fait PAS.
 */
export const IconEye = (p: IconProps) => (
  <Svg {...p}>
    <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6Z" />
    <circle cx="12" cy="12" r="2.6" />
  </Svg>
)

/*
 * Des curseurs, pas une roue crantée.
 *
 * La roue était un cercle avec huit rayons — le MÊME dessin que le soleil du
 * thème. À quinze pixels, côte à côte dans le pied de la barre latérale, ça
 * faisait deux soleils et personne ne pouvait devenir lequel réglait quoi.
 * Trois curseurs ne ressemblent à rien d'autre dans ce jeu d'icônes.
 */
export const IconSettings = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 7h10M18 7h2M4 12h4M12 12h8M4 17h9M17 17h3" />
    <circle cx="16" cy="7" r="2" />
    <circle cx="10" cy="12" r="2" />
    <circle cx="15" cy="17" r="2" />
  </Svg>
)

export const IconSearch = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="m15.5 15.5 4 4" />
  </Svg>
)

export const IconChevronLeft = (p: IconProps) => (
  <Svg {...p}>
    <path d="m14.5 6-6 6 6 6" />
  </Svg>
)

export const IconChevronRight = (p: IconProps) => (
  <Svg {...p}>
    <path d="m9.5 6 6 6-6 6" />
  </Svg>
)

/*
 * Le chevron de tri d'une colonne.
 *
 * Xola en met un dans chaque en-tête triable, et c'est l'affordance qui manque
 * le plus à nos tableaux : rien ne dit qu'une colonne se trie, donc personne
 * n'essaie. Il ne s'affiche PLEIN que sur la colonne active — un chevron sur
 * les dix en-têtes ne désigne plus rien.
 */
export const IconChevronDown = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 9.5 6 6 6-6" />
  </Svg>
)

export const IconChevronUp = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 14.5 6-6 6 6" />
  </Svg>
)

export const IconClose = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Svg>
)

export const IconAlert = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 4.5 21 20H3Z" />
    <path d="M12 10v4M12 17h.01" />
  </Svg>
)

export const IconCheck = (p: IconProps) => (
  <Svg {...p}>
    <path d="m5 12.5 4.5 4.5L19 7" />
  </Svg>
)

/**
 * Des plans empilés : plusieurs lieux vus d'au-dessus.
 *
 * Deux losanges décalés plutôt que trois rectangles : la marque du produit est
 * un losange (le canot vu du dessus), donc l'icône du groupe en répète la
 * géométrie au lieu d'importer une pile de cartes générique.
 */
export const IconLayers = (p: IconProps) => (
  <svg
    width={p.size ?? 16}
    height={p.size ?? 16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3 21 8l-9 5-9-5 9-5z" />
    <path d="M3 14l9 5 9-5" />
  </svg>
)

export const IconPlus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
)

export const IconMinus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 12h14" />
  </Svg>
)

export const IconDownload = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3v11m0 0 4-4m-4 4-4-4" />
    <path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
  </Svg>
)

/**
 * La marque : un rabaska vu de dessus. Deux arcs pour la coque, une nervure
 * pour la quille, deux traits pour les pagaies. C'est le seul dessin de
 * l'application qui n'est pas une icône fonctionnelle, et il porte l'accent.
 */
export const MarkPortage = ({ size = 22 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M12 2.5c3.2 3.4 4.6 6.6 4.6 9.5s-1.4 6.1-4.6 9.5C8.8 18.1 7.4 14.9 7.4 12S8.8 5.9 12 2.5Z" />
    <path d="M12 5.5v13" strokeWidth="1" opacity="0.55" />
    <path d="M3 9.5 7.6 12M21 9.5 16.4 12" strokeWidth="1" opacity="0.45" />
  </svg>
)

/* Les trois états de la bascule de thème : suivre le système, clair, sombre. */
export const IconMonitor = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2.5" y="4" width="19" height="12.5" rx="2" />
    <path d="M8.5 20.5h7M12 16.5v4" />
  </svg>
)

export const IconSun = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2v2.4M12 19.6V22M2 12h2.4M19.6 12H22M4.9 4.9l1.7 1.7M17.4 17.4l1.7 1.7M19.1 4.9l-1.7 1.7M6.6 17.4l-1.7 1.7" />
  </svg>
)

export const IconMoon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.5 14.4A8.6 8.6 0 1 1 9.6 3.5a6.9 6.9 0 0 0 10.9 10.9Z" />
  </svg>
)

/* L'avion en papier de l'envoi : le seul pictogramme universellement lu comme
   « ceci part vers des gens ». */
export const IconSend = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.5 2.5 11 13" />
    <path d="M21.5 2.5 15 21.5l-4-8.5-8.5-4Z" />
  </svg>
)

/* Un cadre de visée : ce que tout le monde reconnaît comme « scanner ». */
export const IconScan = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 8V5.5A2.5 2.5 0 0 1 5.5 3H8M16 3h2.5A2.5 2.5 0 0 1 21 5.5V8M21 16v2.5a2.5 2.5 0 0 1-2.5 2.5H16M8 21H5.5A2.5 2.5 0 0 1 3 18.5V16" />
    <path d="M3.5 12h17" />
  </svg>
)

/* Une planche à pince : le formulaire qu'on remplit à l'entrée. */
export const IconClipboard = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3.5h6a1 1 0 0 1 1 1V6H8V4.5a1 1 0 0 1 1-1Z" />
    <path d="M16 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" />
    <path d="M8.5 12h7M8.5 16h4.5" />
  </svg>
)

/* Un globe : le site public, par opposition au back-office. */
export const IconGlobe = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M3.5 9.5h17M3.5 14.5h17" />
    <path d="M12 3c-2.4 2.4-3.6 5.4-3.6 9s1.2 6.6 3.6 9c2.4-2.4 3.6-5.4 3.6-9S14.4 5.4 12 3Z" />
  </svg>
)

/* Un tracé de moniteur : ce qui bat en direct. */
export const IconPulse = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2.5 12h4l2-5 3.5 10L15 12h6.5" />
  </svg>
)

/*
 * L'analytique : des colonnes de hauteurs INÉGALES.
 *
 * Trois barres égales se lisent comme une pile ou un menu, pas comme un
 * graphique — c'est la différence de hauteur qui porte le sens, et elle doit
 * survivre à 16 px.
 */
export const IconChart = ({ size = 16, className }: IconProps) => (
  <Svg size={size} className={className}>
    <path d="M3 21h18" />
    <path d="M6.5 21V13" />
    <path d="M12 21V6" />
    <path d="M17.5 21v-5.5" />
  </Svg>
)

/* Le téléversement : une flèche qui SORT du plateau, l'inverse exact de
   `IconDownload`. Les deux se croisent souvent dans une même barre d'outils,
   donc la symétrie compte plus que l'invention. */
export const IconUpload = ({ size = 16, className }: IconProps) => (
  <Svg size={size} className={className}>
    <path d="M12 16V4" />
    <path d="m7.5 8.5 4.5-4.5 4.5 4.5" />
    <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
  </Svg>
)

/*
 * La déconnexion : une porte avec une flèche qui SORT.
 *
 * La flèche pointe vers l'extérieur du cadre, pas vers l'intérieur — c'est le
 * seul détail qui distingue « se déconnecter » de « se connecter », et il se
 * lit à 16 px. Un icône seul doit donc être sans ambiguïté : il porte aussi un
 * libellé accessible côté appelant.
 */
/**
 * SORTIR d'un plein écran : quatre flèches qui rentrent.
 *
 * Pas la porte de `IconSignOut` — elle veut dire « se déconnecter », et sur
 * l'écran d'une personne qui tient la porte, se tromper entre « quitter le
 * mode » et « quitter sa session » coûte une reconnexion avec une file devant
 * soi. Une croix ne disait pas non plus ce qu'elle ferme : on peut croire
 * qu'elle referme le verdict.
 */
export const IconExit = ({ size = 16, className }: IconProps) => (
  <Svg size={size} className={className}>
    <path d="M9 4.5v4.5H4.5" />
    <path d="M3.5 3.5 9 9" />
    <path d="M15 4.5v4.5h4.5" />
    <path d="M20.5 3.5 15 9" />
    <path d="M9 19.5V15H4.5" />
    <path d="M3.5 20.5 9 15" />
    <path d="M15 19.5V15h4.5" />
    <path d="M20.5 20.5 15 15" />
  </Svg>
)

/** Une CLÉ : ce qu'un build serveur présente pour émettre. */
export const IconKey = ({ size = 16, className }: IconProps) => (
  <Svg size={size} className={className}>
    <circle cx="8" cy="12" r="3.5" />
    <path d="M11.5 12H20" />
    <path d="M17 12v3" />
    <path d="M20 12v2.5" />
  </Svg>
)

/** Des CURSEURS : les réglages d'un appareil. */
export const IconSliders = ({ size = 16, className }: IconProps) => (
  <Svg size={size} className={className}>
    <path d="M4 8h10" />
    <path d="M18 8h2" />
    <circle cx="16" cy="8" r="2" />
    <path d="M4 16h4" />
    <path d="M12 16h8" />
    <circle cx="10" cy="16" r="2" />
  </Svg>
)

export const IconSignOut = ({ size = 16, className }: IconProps) => (
  <Svg size={size} className={className}>
    <path d="M9.5 4.5H6a1.5 1.5 0 0 0-1.5 1.5v12A1.5 1.5 0 0 0 6 19.5h3.5" />
    <path d="M15 8.5 19 12l-4 3.5" />
    <path d="M19 12H9.5" />
  </Svg>
)

/*
 * Un QR : le code qu'on MONTRE, par opposition à la douchette qui LIT.
 *
 * Il ne reprend pas `IconScan` — le cadre à réticule — parce que les deux
 * gestes sont opposés et voisins dans la même barre d'onglets : l'un ouvre
 * l'appareil photo, l'autre affiche une image à faire flasher par quelqu'un
 * d'autre. Deux entrées de navigation qui portent le même pictogramme sont
 * deux entrées qu'on choisit au hasard.
 */
export const IconQr = ({ size = 16 }: { size?: number }) => (
  <Svg size={size}>
    <rect x="3.5" y="3.5" width="6.5" height="6.5" rx="1.2" />
    <rect x="14" y="3.5" width="6.5" height="6.5" rx="1.2" />
    <rect x="3.5" y="14" width="6.5" height="6.5" rx="1.2" />
    <path d="M14 14h3v3h-3zM20.5 14v3M17.5 20.5h3M14 20.5h.01" />
  </Svg>
)

/*
 * LES RÉGLAGES D'UN CASQUE, une icône par clé.
 *
 * Treize lignes de texte alignées se lisent en les lisant TOUTES : l'œil n'a
 * aucun repère pour retrouver le volume sans parcourir les douze autres, et
 * c'est un écran qu'on ouvre avec quelqu'un qui attend. Une forme se retrouve
 * du coin de l'œil.
 *
 * Chacune dessine CE QUE LE RÉGLAGE FAIT et non l'objet sur lequel il agit :
 * un haut-parleur, pas un casque ; un œil barré, pas une caméra. Douze
 * variations du même casque ne donneraient aucun repère — ce serait la même
 * tache treize fois.
 */

export const IconVolume = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 9v6h3l5 4V5L7 9H4Z" />
    <path d="M16 9a4 4 0 0 1 0 6" />
    <path d="M19 6a8 8 0 0 1 0 12" />
  </Svg>
)

export const IconBrightness = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19" />
  </Svg>
)

/* La veille : une lune, parce que le réglage AUTORISE le sommeil. */
export const IconSleep = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" />
  </Svg>
)

/* L'écart pupillaire : deux yeux et la distance entre eux, qui EST le réglage. */
export const IconIpd = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="7" cy="12" r="2.5" />
    <circle cx="17" cy="12" r="2.5" />
    <path d="M7 18h10M7 16.5v3M17 16.5v3" />
  </Svg>
)

/* L'IPD automatique : la même distance, mesurée toute seule. */
export const IconIpdAuto = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="7" cy="10" r="2.5" />
    <circle cx="17" cy="10" r="2.5" />
    <path d="M5 17.5c2.5 2 4.5 2 7 0s4.5-2 7 0" />
  </Svg>
)

export const IconCapture = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="6" width="18" height="13" rx="2" />
    <circle cx="12" cy="12.5" r="3.5" />
    <path d="M9 6l1.5-2h3L15 6" />
  </Svg>
)

/* L'image refusée (08/10) : l'appareil photo, barré. */
export const IconCaptureOff = (p: IconProps) => (
  <Svg {...p}>
    <path d="M7 6H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12" />
    <path d="M21 16.5V8a2 2 0 0 0-2-2h-4l-1.5-2h-3l-1 1.3" />
    <path d="M9.6 10.2a3.5 3.5 0 0 0 4.9 4.9" />
    <path d="M3 3l18 18" />
  </Svg>
)

/* Un crayon : modifier une valeur sur place. */
export const IconPencil = (p: IconProps) => (
  <Svg {...p}>
    <path d="M15.5 4.5l4 4L8 20H4v-4z" />
    <path d="M13.5 6.5l4 4" />
  </Svg>
)

/* Trois points : un menu. */
export const IconMore = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="5.5" cy="12" r="1.2" />
    <circle cx="12" cy="12" r="1.2" />
    <circle cx="18.5" cy="12" r="1.2" />
  </Svg>
)

/* Recentrer : une cible, qui est le geste et non l'appareil. */
export const IconRecenter = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="7" />
    <circle cx="12" cy="12" r="1.5" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
  </Svg>
)

/* Le passthrough : voir à travers. Un œil, et le monde derrière. */
export const IconPassthrough = (p: IconProps) => (
  <Svg {...p}>
    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
    <circle cx="12" cy="12" r="2.5" />
  </Svg>
)

/*
 * La frontière : un périmètre TRACÉ et quelqu'un dedans.
 *
 * Le premier dessin était un volume hexagonal avec un trait pointillé au
 * milieu — vu à seize pixels, ça ne disait rien du tout. Ce qui fait une
 * frontière n'est pas la forme du volume, c'est le trait discontinu au sol et
 * le fait qu'on soit à l'intérieur.
 */
export const IconBoundary = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3.5" y="3.5" width="17" height="17" rx="2.5" strokeDasharray="3 3" />
    <circle cx="12" cy="12" r="2.5" />
  </Svg>
)

/* Le rafraîchissement : une onde, plus serrée à droite. */
export const IconRefreshRate = (p: IconProps) => (
  <Svg {...p}>
    <path d="M2 12c1.2-4 2.4-4 3.6 0s2.4 4 3.6 0" />
    <path d="M12 12c.8-3 1.6-3 2.4 0s1.6 3 2.4 0s1.6-3 2.4 0" />
  </Svg>
)

/* Le kiosque : un cadenas, parce que le réglage VERROUILLE les touches. */
export const IconKiosk = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4" y="10" width="16" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </Svg>
)

/* Le grand espace : la carte d'une salle plus vaste que la pièce. */
export const IconLargeSpace = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 7l6-3 6 3 6-3v13l-6 3-6-3-6 3V7Z" />
    <path d="M9 4v13M15 7v13" />
  </Svg>
)

/*
 * La vue en direct : un écran, et les ondes qui l'alimentent depuis le coin.
 *
 * Le premier dessin translatait deux arcs hors de leur cadre et rendait une
 * forme qu'on ne reconnaissait pas. Les arcs partent maintenant du coin bas
 * gauche, là où tout le monde a appris à les lire.
 */
export const IconCast = (p: IconProps) => (
  <Svg {...p}>
    <rect x="8" y="3.5" width="12.5" height="9.5" rx="1.5" />
    <path d="M3.5 16.5a4 4 0 0 1 4 4" />
    <path d="M3.5 12.5a8 8 0 0 1 8 8" />
    <circle cx="3.7" cy="20.3" r="0.5" />
  </Svg>
)

/* L'heure d'une lecture. `IconToday` est le même dessin mais porte le SENS
   « aujourd'hui » dans la navigation — réutiliser une icône dont le nom dit
   autre chose ferait diverger les deux le jour où l'une des deux bouge. */
export const IconClock = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Svg>
)

/* L'espace disque d'un casque : un tiroir de stockage, un voyant. */
export const IconDisk = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="13" width="18" height="7" rx="2" />
    <path d="M5 13l2.5-8h9L19 13" />
    <path d="M16.5 16.5h.01" />
  </Svg>
)

/*
 * LES ICÔNES DU TABLEAU D'EXPLOITATION.
 *
 * Elles n'ornent pas : sur une bande de quarante cartes, la forme se lit avant
 * le mot, et c'est la forme qui permet de balayer l'écran sans le lire. Chaque
 * fait de la carte en a une, et un fait sans icône est un fait qu'on ne
 * trouvera pas en trois secondes debout derrière un comptoir.
 */

/** Le casque lui-même : l'objet qu'on tend à quelqu'un. */
export const IconHeadset = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 13v-1a8 8 0 0 1 16 0v1" />
    <rect x="2.5" y="13" width="5" height="6" rx="1.6" />
    <rect x="16.5" y="13" width="5" height="6" rx="1.6" />
  </Svg>
)

/** La batterie. Le remplissage est peint par la couleur du bloc, pas ici. */
export const IconBattery = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2" y="7" width="17" height="10" rx="2.5" />
    <path d="M21.5 10.5v3" />
  </Svg>
)

/**
 * LA MAIN TENDUE : « on a dû aider ».
 *
 * Pas une croix rouge ni un point d'exclamation. Aider quelqu'un n'est pas un
 * incident — c'est le métier — et une icône d'alarme ferait hésiter avant de
 * cliquer, donc ferait perdre la trace qu'on cherche à garder.
 */
export const IconHelpHand = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 11V4.8a1.4 1.4 0 0 1 2.8 0V11" />
    <path d="M11.8 10.4V3.6a1.4 1.4 0 0 1 2.8 0v7.4" />
    <path d="M14.6 11V6.2a1.4 1.4 0 0 1 2.8 0V14a6 6 0 0 1-6 6h-.7a5 5 0 0 1-3.9-1.9l-3-3.8a1.5 1.5 0 0 1 2.2-2L9 14.4" />
  </Svg>
)

/** Le chapitre courant de l'œuvre : un signet, pas une barre de progression —
    le graphe d'une œuvre boucle et peut reculer, donc rien n'y « avance ». */
export const IconScene = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 3h12a1 1 0 0 1 1 1v16l-7-4-7 4V4a1 1 0 0 1 1-1z" />
  </Svg>
)

/** Le casque est SUR LA TÊTE. « Au chapitre trois mais posé sur la table »
    est exactement ce qu'un opérateur cherche, et c'est déjà mesuré. */
export const IconWorn = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="7.5" r="3.5" />
    <path d="M5 20a7 7 0 0 1 14 0" />
  </Svg>
)

/** Le départ d'une œuvre. */
export const IconPlay = (p: IconProps) => (
  <Svg {...p}>
    <path d="M8 5.5v13l10-6.5-10-6.5z" />
  </Svg>
)

/** Écrire à quelqu'un dans son casque : une bulle, la forme que tout le monde lit. */
export const IconMessage = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4.5 6.5A2 2 0 0 1 6.5 4.5h11a2 2 0 0 1 2 2v7.5a2 2 0 0 1-2 2H10l-4.5 3.5v-3.5h0a1 1 0 0 1-1-1z" />
  </Svg>
)

/** La pause d'une personne : deux barres, la forme que tout le monde lit. */
export const IconPause = (p: IconProps) => (
  <Svg {...p}>
    <path d="M8.5 5.5v13M15.5 5.5v13" />
  </Svg>
)

/** Le départ qui se fait TOUT SEUL : la même flèche, remise en boucle. */
export const IconAuto = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3.5 12a8.5 8.5 0 0 1 14.5-6" />
    <path d="M20.5 12a8.5 8.5 0 0 1-14.5 6" />
    <path d="M18 2.5V6h-3.5" />
    <path d="M6 21.5V18h3.5" />
  </Svg>
)

/** Le sablier des compteurs. Distinct de `IconToday`, qui est une horloge :
    l'un dit une HEURE, l'autre une DURÉE, et la carte affiche les deux. */
export const IconTimer = (p: IconProps) => (
  <Svg {...p}>
    <path d="M7 3h10" />
    <path d="M7 21h10" />
    <path d="M7 3c0 4 3 5 3 9s-3 5-3 9" />
    <path d="M17 3c0 4-3 5-3 9s3 5 3 9" />
  </Svg>
)

/** Quelqu'un attend : l'étape « arrivé ». */
export const IconWaiting = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5V12l3.2 1.8" />
    <path d="M12 3v1.5" />
  </Svg>
)

/** Le formulaire signé : l'étape « enregistré ». */
export const IconSigned = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 3h8l5 5v8" />
    <path d="M14 3v5h5" />
    <path d="M4 20.5c2-.5 3-2.5 4.5-2.5s1.5 2 3 2 2.5-2 4-2 2 1 3.5 1" />
  </Svg>
)

/** La porte de la salle : l'étape « en salle ». */
export const IconInRoom = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 20V4.8a1 1 0 0 1 .8-1l9-1.7a1 1 0 0 1 1.2 1V20" />
    <path d="M2.5 20h19" />
    <circle cx="12" cy="12.5" r="1" />
  </Svg>
)

/** C'est fini, et c'est une bonne nouvelle : l'étape « terminé ». */
export const IconDone = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20.5 6 9.5 17.5 3.5 11.5" />
  </Svg>
)

/** Le plan du lieu vu de dessus : des surfaces, pas un volume. */
export const IconMap = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 6.5 9 4l6 2.5L21 4v13.5L15 20l-6-2.5L3 20z" />
    <path d="M9 4v13.5" />
    <path d="M15 6.5V20" />
  </Svg>
)

/** La chaleur : un thermomètre, pas une flamme. Une flamme dit « ça brûle »,
    or le bloc affiche aussi des températures parfaitement normales. */
export const IconHeat = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10 13.5V4.5a2 2 0 0 1 4 0v9a4 4 0 1 1-4 0z" />
    <path d="M12 9.5v5" />
  </Svg>
)

/** Un billet de banque : ce qui a été payé. */
export const IconMoney = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2.5" y="6" width="19" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.6" />
    <path d="M6 9.5v.01M18 14.5v.01" />
  </Svg>
)

/** Rendre : une flèche qui revient. */
export const IconRefund = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
  </Svg>
)
